import React, { useCallback, useEffect, useState } from 'react';
import { Button, Grid, IconButton, Menu, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { Add, ChevronLeft, ChevronRight, FirstPage, KeyboardArrowDown, KeyboardArrowUp, LastPage } from '@mui/icons-material';
import { openSnackbar } from 'store/slices/snackbar';
import { useDispatch } from 'react-redux';
import { gridRowCountSelector, gridPaginationModelSelector, useGridApiContext, useGridSelector } from '@mui/x-data-grid-pro';
import { makeStyles } from '@mui/styles';

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100, 200];

const useStyles = makeStyles(() => ({
    paginationItems: { display: 'flex', justifyContent: 'flex-end' },
    paginationArrow: { fontSize: '1rem', fontWeight: '500', color: '#203461', marginLeft: '4px' },
    paginationControl: {
        '&:hover': {
            backgroundColor: 'transparent !important',
            borderRadius: '0 !important'
        },
        '&:disabled': {
            '& > svg': {
                color: '#C3C3C3 !important'
            }
        }
    },
    gridToolbarCustomFont: { fontSize: '0.8125rem', fontWeight: '500', color: '#858585', marginLeft: '4px' }
}));

// export interface LineItemsCustomFooterProps {
//     handlePageChange: any;
//     handlePageSizeChange: (pageSize: number) => Promise<void>;
// }

export type LineItemsCustomFooterProps = {
    onClickNew?: () => void;
};

export const LineItemsCustomFooter = ({ onClickNew }: LineItemsCustomFooterProps) => {
    const storeDispatch = useDispatch();
    const apiRef = useGridApiContext();
    const classes = useStyles();

    const paginationModel = useGridSelector(apiRef, gridPaginationModelSelector);
    const rowCountState = useGridSelector(apiRef, gridRowCountSelector);

    const currentPage = paginationModel.page;
    const currentPageSize = paginationModel.pageSize;

    const [inputValue, setInputValue] = useState((currentPage + 1) as number | null);
    const [applyAnchorEl, setApplyAnchorEl] = useState<HTMLDivElement | null>(null);

    const handlePageChange = useCallback(
        (page: number) => {
            apiRef.current.setPage(page);
        },
        [apiRef]
    );

    const handlePageSizeChange = useCallback(
        (pageSize: number) => {
            apiRef.current.setPageSize(pageSize);
        },
        [apiRef]
    );

    useEffect(() => {
        setInputValue(currentPage + 1);
    }, [currentPage]);

    useEffect(() => {
        const delayInputTimeoutId = setTimeout(() => {
            if (inputValue !== null && inputValue !== currentPage + 1) {
                if (inputValue <= Math.ceil(rowCountState / currentPageSize)) {
                    handlePageChange(inputValue - 1);
                } else {
                    handlePageChange(Math.ceil(rowCountState / currentPageSize) - 1);
                }
            }
        }, 500);
        return () => clearTimeout(delayInputTimeoutId);
    }, [currentPage, currentPageSize, handlePageChange, inputValue, rowCountState]);

    const handleClickButtons = (pageSize: number) => {
        setApplyAnchorEl(null);
        handlePageSizeChange(pageSize);
    };

    const calcPageRecords = () => {
        const start = currentPage * currentPageSize + 1;
        const end = Math.min((currentPage + 1) * currentPageSize, rowCountState);
        return `${start} - ${end} of ${rowCountState}`;
    };
    const shouldNext = currentPage + 1 !== Math.ceil(rowCountState / currentPageSize);
    const shouldPrev = currentPage > 0;

    return (
        <Box component={Grid} container alignItems="center" justifyContent="space-between" spacing={1} sx={{ width: '100%' }}>
            <Grid item xs>
                <Box
                    component={Button}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        background: '#F5F6F7',
                        padding: '8px 12px',
                        borderRadius: '0 100px 100px 0',
                        width: '100%'
                    }}
                    onClick={onClickNew}
                >
                    <Add htmlColor="#858585" />
                    <Typography variant="body1" sx={{ color: '#858585' }} fontWeight={400} component="div">
                        New
                    </Typography>
                </Box>
            </Grid>
            <Grid item xs="auto" container spacing={0} justifyContent="flex-end" alignItems="center">
                <Grid item xs="auto" className={classes.paginationItems}>
                    <Box
                        sx={{
                            mr: '5px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            background: '#F5F6F7',
                            padding: '8px 12px',
                            borderRadius: '100px',
                            cursor: 'pointer'
                        }}
                        onClick={(event) => setApplyAnchorEl(event.currentTarget)}
                    >
                        <Typography variant="body1" sx={{ color: '#858585' }} fontWeight={500} component="div">
                            Rows per page &nbsp;{currentPageSize}
                        </Typography>
                        {applyAnchorEl ? (
                            <KeyboardArrowDown className={classes.paginationArrow} />
                        ) : (
                            <KeyboardArrowUp className={classes.paginationArrow} />
                        )}
                    </Box>
                    <Menu
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'right'
                        }}
                        anchorEl={applyAnchorEl}
                        open={!!applyAnchorEl}
                        onClose={() => setApplyAnchorEl(null)}
                        style={{ padding: 0 }}
                    >
                        {ROWS_PER_PAGE_OPTIONS.map((val) => (
                            <MenuItem
                                key={val}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleClickButtons(val);
                                }}
                            >
                                {val}
                            </MenuItem>
                        ))}
                    </Menu>
                </Grid>
                <Grid
                    item
                    xs="auto"
                    container
                    component={Box}
                    sx={{ mr: '5px', justifyContent: 'flex-end', background: '#F5F6F7', padding: '3px 12px', borderRadius: '100px' }}
                >
                    <Grid item xs="auto" sx={{ display: 'flex', alignItems: 'center' }}>
                        <TextField
                            value={inputValue}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                if (event.target.value) {
                                    if (Number(event.target.value) <= Math.ceil(rowCountState / currentPageSize)) {
                                        if (Number(event.target.value) === 0) {
                                            setInputValue(1);
                                        } else {
                                            setInputValue(Number(event.target.value));
                                        }
                                    } else {
                                        storeDispatch(
                                            openSnackbar({
                                                open: true,
                                                type: 'error',
                                                title: 'Error',
                                                subtitle: `There are only ${Math.ceil(rowCountState / currentPageSize)} pages!`
                                            })
                                        );
                                        setInputValue(Math.ceil(rowCountState / currentPageSize));
                                    }
                                } else {
                                    setInputValue(null);
                                }
                            }}
                            type="number"
                            size="small"
                            sx={{
                                maxWidth: '24px',
                                height: '32px',
                                borderRadius: '8px',
                                '& input': {
                                    textAlign: 'center',
                                    padding: '5px 7px !important'
                                },
                                'input::-webkit-outer-spin-button,input::-webkit-inner-spin-button': {
                                    WebkitAppearance: 'none',
                                    margin: 0
                                }
                            }}
                            InputProps={{
                                inputProps: {
                                    min: 1,
                                    max: Math.ceil(rowCountState / currentPageSize)
                                }
                            }}
                        />
                        <Tooltip title={`${inputValue} of ${Math.ceil(rowCountState / currentPageSize)} ( ${calcPageRecords()} )`}>
                            <Typography
                                sx={{
                                    marginLeft: '5px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    display: 'block'
                                }}
                                variant="body1"
                                className={classes.gridToolbarCustomFont}
                                component="div"
                            >
                                of {Math.ceil(rowCountState / currentPageSize)} ( {calcPageRecords()} )
                            </Typography>
                        </Tooltip>
                    </Grid>
                    <Grid item xs="auto" className={classes.paginationItems}>
                        <IconButton
                            disabled={!shouldPrev}
                            className={classes.paginationControl}
                            size="small"
                            onClick={() => {
                                handlePageChange(0);
                            }}
                        >
                            <FirstPage className={classes.paginationArrow} />
                        </IconButton>
                        <IconButton
                            disabled={!shouldPrev}
                            className={classes.paginationControl}
                            size="small"
                            onClick={() => {
                                handlePageChange(currentPage - 1);
                            }}
                        >
                            <ChevronLeft className={classes.paginationArrow} />
                        </IconButton>
                        <IconButton
                            disabled={!shouldNext}
                            className={classes.paginationControl}
                            size="small"
                            onClick={() => {
                                handlePageChange(currentPage + 1);
                            }}
                        >
                            <ChevronRight className={classes.paginationArrow} />
                        </IconButton>
                        <IconButton
                            disabled={!shouldNext}
                            className={classes.paginationControl}
                            size="small"
                            onClick={() => {
                                handlePageChange(Math.ceil(rowCountState / currentPageSize) - 1);
                            }}
                        >
                            <LastPage className={classes.paginationArrow} />
                        </IconButton>
                    </Grid>
                </Grid>
            </Grid>
        </Box>
    );
};
